// A page can contain several trip cards at once. Keep one decoded video
// active globally so a card that is only partially visible never competes
// with the video the visitor is actually watching.
let activePlayback = null;

// Android's native feed starts with sound enabled, then shares a mute choice
// across its videos for the lifetime of this app session. The in-memory
// default deliberately resets to sound-on when the app is launched again.
// Other platforms still mute autoplay independently in UnifiedVideo.
let mediaAudioUnlocked = false;
let mediaMutedPreference = false;
const mediaMutedPreferenceListeners = new Set();

export const hasMediaAudioUnlock = () => mediaAudioUnlocked;

export const getMediaMutedPreference = () => mediaMutedPreference;

export const unlockMediaAudio = () => {
  mediaAudioUnlocked = true;
  setMediaMutedPreference(false);
};

export const setMediaMutedPreference = (muted) => {
  const nextPreference = Boolean(muted);
  if (mediaMutedPreference === nextPreference) return;
  mediaMutedPreference = nextPreference;
  if (!mediaMutedPreference) mediaAudioUnlocked = true;
  mediaMutedPreferenceListeners.forEach((listener) => listener(mediaMutedPreference));
};

export const subscribeMediaMutedPreference = (listener) => {
  mediaMutedPreferenceListeners.add(listener);
  return () => mediaMutedPreferenceListeners.delete(listener);
};

export const requestMediaPlayback = (owner, media, priority = 0) => {
  if (!owner || !media) return false;

  const normalizedPriority = Number.isFinite(priority) ? priority : 0;
  if (activePlayback?.media === media) {
    activePlayback = { owner, media, priority: normalizedPriority };
    return true;
  }

  // Keep the currently more-visible video playing. A real user interaction
  // passes a higher priority and therefore always wins this comparison.
  if (activePlayback && activePlayback.owner !== owner
    && activePlayback.priority > normalizedPriority) {
    return false;
  }

  try {
    activePlayback?.media?.pause?.();
  } catch (_) {
    // A detached element may already have been disposed by React.
  }
  activePlayback = { owner, media, priority: normalizedPriority };
  return true;
};

export const releaseMediaPlayback = (owner, media) => {
  if (!activePlayback || activePlayback.owner !== owner) return;
  if (media && activePlayback.media !== media) return;
  activePlayback = null;
};

/** Stop the current player when the page/document is hidden or discarded. */
export const stopActiveMediaPlayback = () => {
  try {
    activePlayback?.media?.pause?.();
  } catch (_) {
    // Detached media elements are safe to ignore.
  }
  activePlayback = null;
};
